import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setupPassword } from "../../../api/user";
import { toast } from "react-hot-toast";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { PMField } from "../../../components/utils/PMField";

export const SetupPassword = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  useEffect(() => {
    if (!token || !email) {
      toast.error("Invalid link. Token and email are required.");
      navigate("/authentication/login");
    }
  }, [token, email, navigate]);

  if (!token || !email) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Invalid Link
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              This link is invalid or missing required parameters.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {success ? "Password Set Successfully!" : "Set Up Your Password"}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {success
              ? "Your password has been set. You can now log in."
              : "Please set a password for your account to continue."}
          </p>
        </div>

        {success ? (
          <div className="mt-8">
            <button
              onClick={() => navigate("/authentication/login")}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Go to Login
            </button>
          </div>
        ) : (
          <Formik
            initialValues={{
              password: "",
              passwordConfirmation: "",
            }}
            validationSchema={Yup.object({
              password: Yup.string()
                .min(8, "Password must be at least 8 characters")
                .required("Password is required"),
              passwordConfirmation: Yup.string()
                .oneOf([Yup.ref("password"), null], "Passwords must match")
                .required("Password confirmation is required"),
            })}
            onSubmit={async (values, { setSubmitting, setErrors }) => {
              setLoading(true);
              try {
                const response = await setupPassword(
                  token,
                  email,
                  values.password,
                  values.passwordConfirmation
                );

                if (response.errors) {
                  setErrors({ submit: response.errors.join(", ") });
                  toast.error(response.errors.join(", "));
                } else {
                  setSuccess(true);
                  toast.success("Password set successfully!");
                }
              } catch (error) {
                const errorMessage =
                  error?.response?.data?.errors?.join(", ") ||
                  error?.message ||
                  "Failed to set password";
                setErrors({ submit: errorMessage });
                toast.error(errorMessage);
              } finally {
                setLoading(false);
                setSubmitting(false);
              }
            }}
          >
            {(formik) => (
              <Form className="mt-8 space-y-6">
                <div className="rounded-md shadow-sm -space-y-px">
                  <PMField
                    name="password"
                    type="password"
                    label="Password"
                    required
                    {...formik}
                  />
                  <div className="mt-4">
                    <PMField
                      name="passwordConfirmation"
                      type="password"
                      label="Confirm Password"
                      required
                      {...formik}
                    />
                  </div>
                </div>

                {formik.errors.submit && (
                  <div className="rounded-md bg-red-50 p-4">
                    <div className="text-sm text-red-800">
                      {formik.errors.submit}
                    </div>
                  </div>
                )}

                <div>
                  <button
                    type="submit"
                    disabled={loading || formik.isSubmitting}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Setting Password..." : "Set Password"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </div>
  );
};

